import { Pie, type PieCustomLayer } from "@nivo/pie";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  EMPTY_HATCH_CSS,
  EMPTY_HATCH_DEF,
  isEmptyGroup,
  PIE_COLOR_ORDER,
} from ".";

import { ChartData } from "@/__generated__/graphql";
import {
  formatValue,
  toPieData,
  useAnimatesOnChange,
  usePlotSize,
} from "@/components/organisms/Chart/Chart.utils";
import { ChartTooltip } from "@/components/organisms/Chart/ChartTooltip";
import { typographyStyles } from "@/components/ui/typography";

export const PIE_SIZE_CONFIGS = {
  xs: {
    margin: { top: 4, right: 4, bottom: 4, left: 4 },
    activeOuterRadiusOffset: 4,
    labelOffset: 16,
    borderWidth: 1,
    cornerRadius: 2,
    innerRadius: 0.4,
    arcLabelsSkipAngle: 30,
  },
  sm: {
    margin: { top: 6, right: 6, bottom: 6, left: 6 },
    activeOuterRadiusOffset: 6,
    labelOffset: 24,
    borderWidth: 1.5,
    cornerRadius: 3,
    innerRadius: 0.45,
    arcLabelsSkipAngle: 25,
  },
  md: {
    margin: { top: 8, right: 8, bottom: 8, left: 8 },
    activeOuterRadiusOffset: 8,
    labelOffset: 32,
    borderWidth: 2,
    cornerRadius: 4,
    innerRadius: 0.5,
    arcLabelsSkipAngle: 20,
  },
  lg: {
    margin: { top: 10, right: 10, bottom: 10, left: 10 },
    activeOuterRadiusOffset: 10,
    labelOffset: 40,
    borderWidth: 2.5,
    cornerRadius: 5,
    innerRadius: 0.55,
    arcLabelsSkipAngle: 15,
  },
};

/**
 * A slice's figure, in a rounded chip the width of what it says.
 *
 * Measured rather than guessed: the text is drawn once invisibly, its box
 * read, and the background sized to it - a chip worked out from the number of
 * characters is either too tight around a wide number or padded for one.
 */
function ArcChip({
  x,
  y,
  said,
  font,
  fontSize,
  color,
  background,
  border,
}: {
  x: number;
  y: number;
  said: string;
  font: string;
  fontSize: string;
  color: string;
  background: string;
  border: string;
}) {
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(0);

  useLayoutEffect(() => {
    if (textRef.current) setTextWidth(textRef.current.getBBox().width);
  }, [said, font, fontSize]);

  const width = textWidth + 16;

  return (
    <g transform={`translate(${x}, ${y})`} pointerEvents="none">
      <text ref={textRef} style={{ fontFamily: font, fontSize, opacity: 0 }}>
        {said}
      </text>
      <rect
        x={-width / 2}
        y={-12}
        width={width}
        height={24}
        rx="8"
        ry="8"
        fill={background}
        stroke={border}
        strokeWidth="1"
      />
      <text
        textAnchor="middle"
        alignmentBaseline="middle"
        style={{ fontFamily: font, fontSize, fill: color }}
      >
        {said}
      </text>
    </g>
  );
}

export function ChartPie({
  data,
  size = "md",
  colourOf,
}: {
  data: ChartData;
  size?: "xs" | "sm" | "md" | "lg";
  /** What each slice is drawn in, by the name it stands for. */
  colourOf?: (label: string) => string;
}) {
  const sizeConfig = PIE_SIZE_CONFIGS[size];
  // Every slice it was given: the server caps a pie at a handful and rolls
  // the tail into one "Other", so dropping slices here would drop money out
  // of the whole without saying so.
  const pieData = useMemo(() => toPieData(data), [data]);
  // What each slice was drawn in, for the swatches beside the figures: the
  // slice with nothing in it is hatched there as it is in the pie. Asked of
  // the same thing the slices ask - ``colourOf`` - rather than worked out
  // again by position: the empty slice takes none of the palette, so counted
  // by position every name after it showed one colour along from its slice.
  const sliceColors = useMemo(
    () =>
      pieData.map((slice, index) =>
        isEmptyGroup(slice.id)
          ? EMPTY_HATCH_CSS
          : colourOf
            ? colourOf(String(slice.id))
            : PIE_COLOR_ORDER[index % PIE_COLOR_ORDER.length],
      ),
    [pieData, colourOf],
  );

  const {
    ref: wrapperRef,
    width: plotWidth,
    height: plotHeight,
  } = usePlotSize();
  // Animated when the numbers change, never when the box does.
  const animates = useAnimatesOnChange(data);

  /**
   * The figures on the slices, drawn outside the chart's own motion.
   *
   * Nivo keeps a departing label on screen for as long as the arcs take to
   * settle and fades the arriving one in over it, so a pie that has been asked
   * a slightly different question reads as two pies at once for half a second.
   * The slices should animate - that is the answer changing - but what they
   * are worth is a number, and it simply changes.
   */
  const instantLabels: PieCustomLayer<{
    id: string;
    value: number;
    said: number;
  }> = ({ dataWithArc, centerX, centerY }) => (
    <g transform={`translate(${centerX}, ${centerY})`}>
      {dataWithArc
        .filter((slice) => slice.arc.angleDeg >= sizeConfig.arcLabelsSkipAngle)
        .map((slice) => {
          const middle =
            (slice.arc.startAngle + slice.arc.endAngle) / 2 - Math.PI / 2;
          const distance = slice.arc.innerRadius + sizeConfig.labelOffset;
          return (
            <ArcChip
              key={slice.id}
              x={Math.cos(middle) * distance}
              y={Math.sin(middle) * distance}
              said={formatValue(slice.data.said, data.valueFormat)}
              font={typographyStyles["title-sm"].fontFamily as string}
              fontSize={typographyStyles["title-sm"].fontSize as string}
              color={"var(--foreground)"}
              background={"var(--muted)"}
              border={"var(--border)"}
            />
          );
        })}
    </g>
  );

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        // An <svg> is an inline box, so the line it sits on adds a few pixels
        // of descender under it and the card it is on finds something to
        // scroll. Laid out as a block there is nothing under it.
        display: "flex",
        overflow: "hidden",
      }}
    >
      {plotWidth > 0 && plotHeight > 0 && (
        <Pie
          width={plotWidth}
          height={plotHeight}
          animate={animates}
          transitionMode="startAngle"
          id="id"
          data={pieData}
          // By which slice it is, not where it sits: hiding one must not
          // repaint the others.
          colors={
            colourOf
              ? ({ id }: { id: string | number }) => colourOf(String(id))
              : PIE_COLOR_ORDER
          }
          // The slice for rows the grouping says nothing about is struck in grey
          // rather than coloured - a share of the whole with nothing to name.
          defs={[EMPTY_HATCH_DEF]}
          fill={pieData
            .filter((slice) => isEmptyGroup(slice.id))
            .map((slice) => ({
              match: { id: slice.id },
              id: EMPTY_HATCH_DEF.id,
            }))}
          value="value"
          // Nivo sizes the pie to its box and then draws the hovered slice
          // ``activeOuterRadiusOffset`` past that, so the room that offset needs
          // is kept on every side here. Eight more underneath again: the pie
          // otherwise sits on the card's floor with nothing between them.
          margin={{
            top: sizeConfig.margin.top + sizeConfig.activeOuterRadiusOffset,
            right: sizeConfig.margin.right + sizeConfig.activeOuterRadiusOffset,
            bottom:
              sizeConfig.margin.bottom + sizeConfig.activeOuterRadiusOffset + 8,
            left: sizeConfig.margin.left + sizeConfig.activeOuterRadiusOffset,
          }}
          innerRadius={sizeConfig.innerRadius}
          borderColor={"var(--muted)"}
          borderWidth={sizeConfig.borderWidth}
          cornerRadius={sizeConfig.cornerRadius}
          activeOuterRadiusOffset={sizeConfig.activeOuterRadiusOffset}
          layers={["arcs", instantLabels, "legends"]}
          tooltip={({ datum }) => {
            return (
              <ChartTooltip
                values={pieData}
                datum={datum}
                size={size}
                colors={sliceColors}
                checkIsActiveValue={(d: any) => d.id === datum.id}
                keyColumnHeader={data.title}
                valueColumnHeader="Value"
                renderKeyColumn={(_hovered, slice: any) => slice.id}
                // Each row is a slice of its own: read off the row, not off the
                // slice under the pointer, or every line says the same figure.
                renderValueColumn={(_hovered, slice: any) =>
                  formatValue(slice.said, data.valueFormat)
                }
              />
            );
          }}
        />
      )}
    </div>
  );
}
